import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, Table } from 'react-bootstrap';
import { isChangeManager } from './changePermissions';
import { useAuth } from '../../context/AuthContext';
import { useModal } from '../../context/ModalContext';
import { changesApi } from '../../services/changesApi';
import type { ChangeApprovalFlowConfig, ChangeWorkGroup } from '../../types/changes';
import { getApiErrorMessage } from '../../utils/apiError';
import ChangeApprovalFlowEditor from './components/ChangeApprovalFlowEditor';

const ChangeApprovalFlowHubPage: React.FC = () => {
  const { user } = useAuth();
  const { showConfirm } = useModal();
  const canManage = isChangeManager(user);
  const [items, setItems] = useState<ChangeApprovalFlowConfig[]>([]);
  const [areas, setAreas] = useState<ChangeWorkGroup[]>([]);
  const [typeOptions, setTypeOptions] = useState<{ code: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChangeApprovalFlowConfig | null>(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listPayload, workGroups, typesPayload] = await Promise.all([
        changesApi.listApprovalFlowConfigs(),
        changesApi.listWorkGroups({ only_active: true }).catch(() => ({ items: [] })),
        changesApi.listTypePolicies({ only_active: true }).catch(() => ({ items: [] })),
      ]);
      setItems(listPayload.items || []);
      setAreas(workGroups.items || []);
      setTypeOptions(
        (typesPayload.items || []).map((item) => ({
          code: item.code,
          name: item.name || item.code,
        })),
      );
      setError(null);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Falha ao carregar configurações.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const typeLabel = (row: ChangeApprovalFlowConfig) =>
    row.change_type_code ? row.change_type_name || row.change_type_code : 'Sem tipo';

  const toggleActive = async (row: ChangeApprovalFlowConfig) => {
    if (!canManage) return;
    try {
      await changesApi.setApprovalFlowConfigActive(row.id, !row.is_active);
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível alterar o status.'));
    }
  };

  const backToList = () => {
    setEditing(null);
    setCreating(false);
    setError(null);
  };

  const remove = async (row: ChangeApprovalFlowConfig) => {
    const ok = await showConfirm({
      title: 'Excluir aprovação',
      message: `Deseja excluir a aprovação "${row.name}"?`,
      details: 'Esta ação não pode ser desfeita. Mudanças em andamento que usam esta regra continuarão com os dados já gravados até nova configuração.',
      variant: 'danger',
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    try {
      await changesApi.deleteApprovalFlowConfig(row.id);
      if (editing?.id === row.id) setEditing(null);
      await load();
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Não foi possível excluir.'));
    }
  };

  const startCreate = () => {
    setCreating(true);
    setEditing({
      id: 0,
      name: '',
      change_type_code: null,
      is_active: true,
      use_immediate_manager: false,
      parallel_after_first: true,
      steps: [],
      managers: [],
    });
  };

  if (loading && items.length === 0 && !editing) {
    return <p className="text-muted">Carregando…</p>;
  }

  if (editing) {
    return (
      <div>
        {error ? (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        ) : null}
        <ChangeApprovalFlowEditor
          key={creating ? 'new' : `config-${editing.id}`}
          config={editing}
          areas={areas}
          typeOptions={typeOptions}
          canManage={canManage}
          isNew={creating}
          onBack={backToList}
          onSaved={async () => {
            backToList();
            await load();
          }}
        />
      </div>
    );
  }

  return (
    <div>
      {error ? (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <p className="text-muted small mb-0">
          Uma linha por tipo de mudança. Com <strong>Ativa</strong> ligada, aplica as áreas e gestores
          configurados em Transporte. Desligada, este tipo não usa esta regra (configure áreas antes
          de ligar). Tipos extras: <strong>Nova aprovação</strong> ou <strong>Sem tipo</strong> como
          fallback.
        </p>
        {canManage ? (
          <Button size="sm" onClick={startCreate}>
            Nova aprovação
          </Button>
        ) : null}
      </div>

      <Table responsive size="sm" className="align-middle mb-4">
        <thead>
          <tr>
            <th>Nome</th>
            <th>Tipo</th>
            <th>Áreas (Transporte)</th>
            <th title="Ligada = usa áreas/gestores salvos; desligada = não aplica esta regra">Ativa</th>
            {canManage ? <th className="text-end">Ações</th> : null}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td colSpan={canManage ? 5 : 4} className="text-muted">
                Nenhuma aprovação cadastrada. Use &quot;Nova aprovação&quot; para criar a primeira.
              </td>
            </tr>
          ) : (
            items.map((row) => (
              <tr key={row.id}>
                <td>{row.name}</td>
                <td>{typeLabel(row)}</td>
                <td>{row.steps.filter((s) => s.is_active).length}</td>
                <td>
                  {canManage ? (
                    <Form.Check
                      type="switch"
                      id={`approval-flow-active-${row.id}`}
                      checked={row.is_active}
                      onChange={() => void toggleActive(row)}
                      aria-label={`Ativa ${row.name}`}
                    />
                  ) : row.is_active ? (
                    <Badge bg="success">Sim</Badge>
                  ) : (
                    <Badge bg="secondary">Não</Badge>
                  )}
                </td>
                {canManage ? (
                  <td className="text-end">
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="me-1"
                      onClick={async () => {
                        try {
                          const detail = await changesApi.getApprovalFlowConfig(row.id);
                          setCreating(false);
                          setEditing(detail);
                        } catch (err: unknown) {
                          setError(getApiErrorMessage(err, 'Falha ao abrir configuração.'));
                        }
                      }}
                    >
                      Editar
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => void remove(row)}>
                      Excluir
                    </Button>
                  </td>
                ) : null}
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>
  );
};

export default ChangeApprovalFlowHubPage;
